import type { KeyValueObject } from "$/lib/types/misc.types";
import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function convertTohHumanFileSize(size: number) {
  const i = size == 0 ? 0 : Math.floor(Math.log(size) / Math.log(1024));
  return (
    +(size / Math.pow(1024, i)).toFixed(2) * 1 +
    " " +
    ["B", "KB", "MB", "GB", "TB"][i]
  );
}

export function isPrimitive(
  value: unknown,
): value is number | string | boolean {
  return value !== Object(value);
}

export function formatDateWithHours(date: Date, hasSeconds = false) {
  const options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };

  if (hasSeconds) {
    options.second = "2-digit";
  }
  const dateToFormat = new Date(date);

  const dateFormatter = new Intl.DateTimeFormat("fr-FR", options);

  const formattedDate = dateFormatter.format(dateToFormat);

  return formattedDate;
}

export function isKeyValObject(value: unknown): value is KeyValueObject {
  return Object.prototype.toString.call(value) === "[object Object]";
}

/**
 *
 * @param arr
 * @returns
 * array of keys from an array of objects
 * EXCULDING the key "date"
 */
export const getArrayKeys = <T extends KeyValueObject>(
  arr: T[],
  xAxisKey = "date",
  order?: string[],
  sort?: boolean,
) => {
  if (!arr || arr.length === 0) return [];

  const allKeys = new Set<string>();

  arr.forEach((item) => {
    Object.keys(item).forEach((key) => {
      if (key !== xAxisKey) {
        allKeys.add(key);
      }
    });
  });

  let outArr = Array.from(allKeys);

  if (order) {
    const orderedKeys = order.filter((k) =>
      outArr.some((existingKey) => existingKey.includes(k)),
    );

    const remainingKeys = outArr.filter(
      (k) => !orderedKeys.some((ok) => k.includes(ok)),
    );

    outArr = [...orderedKeys, ...remainingKeys];
  }

  if (sort && !order) return outArr.sort();

  return outArr;
};

export const getMinMax = <T extends KeyValueObject>(
  arr: T[],
  keys: string[],
  applyStep = true,
) => {
  const values = arr.reduce(
    (acc, item) => {
      // Convert values to numbers and get the values from arrays
      const values = keys
        .filter((key) => item[key] !== undefined && item[key] !== null)
        .map((key) => {
          const value = item[key];
          if (Array.isArray(value)) return value.map((v) => Number(v));

          return Number(value);
        })
        .flat();

      // Find the absolute max
      const maxValue = Math.max(...values);
      if (maxValue > acc.max.value) {
        acc.max = { value: maxValue };
      }

      // Find the absolute min
      const minValue = Math.min(...values);
      if (minValue < acc.min.value) {
        acc.min = { value: minValue };
      }

      return acc;
    },
    {
      max: { value: -Infinity },
      min: { value: Infinity },
    },
  );

  if (!applyStep)
    return {
      min: { value: Math.floor(values.min.value) },
      max: { value: Math.ceil(values.max.value) },
    };

  const diff = values.max.value - values.min.value;
  const step = Math.ceil(diff / 5);
  const stepValue = Math.ceil(step / 5) * 5;

  const min = Math.floor(values.min.value / stepValue) * stepValue;
  const max = Math.ceil(values.max.value / stepValue) * stepValue;

  return {
    min: { value: min },
    max: { value: max },
  };
};
